import type { FormEvent } from 'react';
import React from 'react';
import { range } from 'lodash-es';

import {
  Checkbox,
  Flex,
  FormField,
  FormHelp,
  IconPlusOutline,
  IconZillowLogo,
  Input,
  Label,
  LabeledControl,
  Paragraph,
  Spacer,
  Switch,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation';
import { Center, HR } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import { Field, PaymentMethodSection } from '../../../models/index';
import { PAYMENT_METHOD } from '../../../constants';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_PAYMENT_METHODS = 5;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: PaymentMethodSection) => void;
};

export function PaymentMethod_v3({ section, setSectionValues, setSection }: Props): JSX.Element {
  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: PaymentMethodSection,
  });

  const errorMsg = section.hasFieldError(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)
    ? PAYMENT_METHOD.ERROR_MESSAGE.ADDRESS
    : null;

  function onRemoveAll(): void {
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function onSelectZillowPayment(): void {
    setSectionValues({
      [PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED]: !section.getFieldValue(
        PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED,
      ),
    });
  }

  function onSelectElectronic(): void {
    const electronicField = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED);

    electronicField ? onRemoveAll() : onAddOne();

    setSectionValues({
      [PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED]: !electronicField,
    });
  }

  function onSelectDirect(): void {
    setSectionValues({
      [PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED]: !section.getFieldValue(
        PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED,
      ),
    });
  }

  function onAddressChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS]: value,
    });
  }

  function setName(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(
      PAYMENT_METHOD.FIELD_NAME.INDIVIDUAL_ELECTRONIC_PAYMENT_NAME,
      Number(index),
    );

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderIndividualPaymentMethod(index: number) {
    const nameField = section.getField(
      Field.getIndexedFieldName(PAYMENT_METHOD.FIELD_NAME.INDIVIDUAL_ELECTRONIC_PAYMENT_NAME, index),
    );

    return (
      <FormField
        marginBottom="sm"
        label={
          <Flex display="flex" justifyContent="space-between" alignItems="center" marginBottom="xs">
            <Label id={`electronic-payment-method-#${index + 1}`}>Service #{index + 1}</Label>
            {index > 0 && (
              <TextButton
                data-index={index}
                data-test="delete-electronic-payment-method"
                onClick={onDeleteOne}
                fontType="bodySmallHeading"
              >
                Delete
              </TextButton>
            )}
          </Flex>
        }
        control={
          <Input
            aria-labelledby={`electronic-payment-method-#${index + 1}`}
            data-index={index}
            data-test="electronic-payment-method-name"
            value={nameField.enteredValue}
            onChange={setName}
          />
        }
      />
    );
  }

  const totalPaymentMethods = section.getTotalIndexes();
  if (totalPaymentMethods == null) {
    return null;
  }

  return (
    <>
      <Spacer marginBottom="xs">
        <Text fontType="h6">Which form(s) of payment will you accept?</Text>
      </Spacer>
      <LabeledControl
        labelPosition="left"
        fluid
        size="lg"
        label={
          <Label>
            <Paragraph fontType="h6">
              <IconZillowLogo fontColor="brand" marginRight="xs" />
              Zillow Payments
            </Paragraph>
            <Paragraph>
              Tenant pays through our online service. Set up quickly using information from your lease.
            </Paragraph>
          </Label>
        }
        control={
          <Switch
            checkbox={
              <Checkbox
                onChange={onSelectZillowPayment}
                checked={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED)}
              />
            }
          />
        }
      />
      <HR />
      <LabeledControl
        labelPosition="left"
        fluid
        size="lg"
        label={
          <Label disabled>
            <Paragraph fontType="h6">Check or money order</Paragraph>
            <Paragraph>Tenant sends payment to either the landlord or the property manager.</Paragraph>
          </Label>
        }
        control={<Switch disabled checkbox={<Checkbox checked />} />}
      />
      <FormField
        marginY="sm"
        label={<Label id="tenant-send-check">To whom should the tenant send check or money order?</Label>}
        control={
          <Textarea
            aria-labelledby="tenant-send-check"
            data-test="tenant-send-check"
            error={errorMsg}
            onChange={onAddressChange}
            value={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)}
            resize="vertical"
            rows="3"
          />
        }
        description={<FormHelp>Recipient’s name, street, city, state, and ZIP Code</FormHelp>}
      />
      <HR />
      <LabeledControl
        labelPosition="left"
        fluid
        size="lg"
        label={
          <Label>
            <Paragraph fontType="h6">Direct deposit</Paragraph>
            <Paragraph>Tenant transfers funds using bank info, including ACH, if you provide them.</Paragraph>
          </Label>
        }
        control={
          <Switch
            checkbox={
              <Checkbox
                onChange={onSelectDirect}
                checked={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED)}
              />
            }
          />
        }
      />
      <HR />
      <LabeledControl
        labelPosition="left"
        fluid
        size="lg"
        label={
          <Label>
            <Paragraph fontType="h6">Other electronic payment</Paragraph>
            <Paragraph>Tenant pays via third-party online service of your choice.</Paragraph>
          </Label>
        }
        control={
          <Switch
            checkbox={
              <Checkbox
                onChange={onSelectElectronic}
                checked={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED)}
              />
            }
          />
        }
      />
      {range(0, totalPaymentMethods).map((index: number) => (
        <React.Fragment key={index}>{renderIndividualPaymentMethod(index)}</React.Fragment>
      ))}
      {totalPaymentMethods > 0 && (
        <Center>
          {totalPaymentMethods < MAXIMUM_PAYMENT_METHODS ? (
            totalPaymentMethods >= 0 && (
              <TextButton icon={<IconPlusOutline />} onClick={onAddOne} data-test="add-another">
                Add another electronic payment service
              </TextButton>
            )
          ) : (
            <Text>
              <Center>
                <span data-test="maximum-reached">
                  You’ve added {MAXIMUM_PAYMENT_METHODS} electronic payment services, which is the maximum allowed.
                </span>
              </Center>
            </Text>
          )}
        </Center>
      )}
    </>
  );
}

PaymentMethod_v3.description = 'Disable check/money order selection';

export default PaymentMethod_v3;
